import type { Runtime } from "../types.ts";
import { BUILTIN_FORBIDDEN_ARGS } from "./forbidden-args.ts";
import type { AdapterDefinition, AdapterSummaryDTO } from "./types.ts";

/**
 * The four built-in adapter definitions (docs/specs/onboarding-teammates.md §7.3), keyed by the `RUNTIMES` ids they
 * cover. `other` is a custom adapter's slot and the legacy `vscode` has no definition. The server's per-runtime tables
 * (`CONFIG_DIR_VAR`, `STATUS_ARGS`, the default bin and egress of `RUNTIME_DEFAULTS`) are derived from these, so a
 * value is written once.
 *
 * `launch.argv` is the template shape: the runners build the real argv from the board config and the task, and the
 * spawn site checks that argv against `forbiddenArgs`.
 */

const CONSERVATIVE = { steer: false, stop: true, cost: "none", subagents: false, advisor: false, commandGuard: false } as const;

export const BUILTIN_ADAPTER_IDS = ["claude-code", "codex", "cursor", "antigravity"] as const;
export type BuiltinAdapterId = (typeof BUILTIN_ADAPTER_IDS)[number];

const forbidden = () => BUILTIN_FORBIDDEN_ARGS.map((a) => ({ ...a }));

export const BUILTIN_ADAPTERS: Readonly<Record<BuiltinAdapterId, AdapterDefinition>> = {
  "claude-code": {
    id: "claude-code",
    label: "Claude Code",
    docsUrl: "https://code.claude.com/docs/en/overview",
    bin: ["claude"],
    versionArgs: ["--version"],
    versionPattern: "(\\d+\\.\\d+\\.\\d+)",
    minVersion: null,
    statusArgs: ["auth", "status"],
    statusParse: { loggedIn: "\"loggedIn\"\\s*:\\s*true" },
    configDirVar: "CLAUDE_CONFIG_DIR",
    loginFiles: [],
    loginHint: "claude auth login",
    installHint: { text: "Install Claude Code", url: "https://code.claude.com/docs/en/setup" },
    launch: {
      argv: ["-p", "--output-format", "stream-json", "--input-format", "stream-json", "--verbose", "--permission-mode", "default", "--mcp-config", "{mcpConfig}", "--strict-mcp-config", "--model", "{model}"],
      promptTransport: "stdin",
      resumeArgv: ["--resume", "{sessionId}"],
      forbiddenArgs: forbidden(),
    },
    mcp: { argv: ["--mcp-config", "{mcpConfig}"] },
    stream: { parser: "claude-stream-json", unparsedLimit: 50 },
    stateMap: {},
    rateLimit: null,
    egressHosts: [],
    sandbox: { needsPty: false, extraReadPaths: [], needsKeychain: false },
    capabilities: { steer: true, stop: true, cost: "exact", subagents: true, advisor: true, commandGuard: true },
  },
  codex: {
    id: "codex",
    label: "Codex",
    docsUrl: "https://developers.openai.com/codex/cli",
    bin: ["codex"],
    versionArgs: ["--version"],
    versionPattern: "(\\d+\\.\\d+\\.\\d+)",
    minVersion: null,
    statusArgs: ["login", "status"],
    statusParse: { loggedIn: "logged in", planWords: ["chatgpt", "claude.ai", "api key"] },
    configDirVar: "CODEX_HOME",
    loginFiles: ["auth.json"],
    loginHint: "codex login",
    installHint: { text: "Install the Codex CLI", url: "https://developers.openai.com/codex/cli" },
    launch: {
      argv: ["exec", "--json", "-C", "{worktree}", "-s", "workspace-write", "-c", "sandbox_workspace_write.network_access=true", "-m", "{model}", "-"],
      promptTransport: "stdin",
      resumeArgv: ["resume", "{sessionId}"],
      forbiddenArgs: forbidden(),
    },
    mcp: { argv: ["-c", "mcp_servers.roomate.url=\"{mcpUrl}\""] },
    stream: { parser: "codex-jsonl", unparsedLimit: 50 },
    stateMap: {},
    rateLimit: null,
    egressHosts: ["api.openai.com", "chatgpt.com", "auth.openai.com"],
    sandbox: { needsPty: false, extraReadPaths: [], needsKeychain: false },
    capabilities: { ...CONSERVATIVE },
  },
  cursor: {
    id: "cursor",
    label: "Cursor",
    docsUrl: "https://cursor.com/docs/cli/overview",
    bin: ["cursor-agent"],
    versionArgs: ["--version"],
    versionPattern: "(\\d+(?:\\.\\d+)+)",
    minVersion: null,
    statusArgs: ["status"],
    statusParse: { loggedIn: "logged in" },
    configDirVar: null,
    loginFiles: [],
    loginHint: "cursor-agent login",
    installHint: { text: "Install the Cursor CLI", url: "https://cursor.com/docs/cli/overview" },
    launch: { argv: [], promptTransport: "stdin", forbiddenArgs: forbidden() },
    mcp: { argv: [] },
    stream: { parser: "lines", unparsedLimit: 50 },
    stateMap: {},
    rateLimit: null,
    egressHosts: [],
    sandbox: { needsPty: false, extraReadPaths: [], needsKeychain: false },
    capabilities: { ...CONSERVATIVE },
  },
  antigravity: {
    id: "antigravity",
    label: "Antigravity",
    docsUrl: "https://antigravity.google/docs",
    bin: ["agy"],
    versionArgs: ["--version"],
    versionPattern: "(\\d+(?:\\.\\d+)+)",
    minVersion: null,
    statusArgs: null,
    statusParse: { loggedIn: "logged in" },
    configDirVar: null,
    loginFiles: [],
    loginHint: "agy",
    installHint: { text: "Install Antigravity", url: "https://antigravity.google/docs" },
    launch: { argv: [], promptTransport: "stdin", forbiddenArgs: forbidden() },
    mcp: { argv: [] },
    stream: { parser: "lines", unparsedLimit: 50 },
    stateMap: {},
    rateLimit: null,
    egressHosts: [],
    sandbox: { needsPty: false, extraReadPaths: [], needsKeychain: false },
    capabilities: { ...CONSERVATIVE },
  },
};

function deepFreeze<T>(v: T): T {
  if (v && typeof v === "object" && !Object.isFrozen(v)) {
    Object.freeze(v);
    for (const child of Object.values(v)) deepFreeze(child);
  }
  return v;
}
// The tables the server derives from these (STATUS_ARGS, LOGIN_FILES, ...) share these arrays by reference.
deepFreeze(BUILTIN_ADAPTERS);

export function isBuiltinAdapterId(id: string): id is BuiltinAdapterId {
  return (BUILTIN_ADAPTER_IDS as readonly string[]).includes(id);
}

/** The definition of a runtime, or null for `other` and the legacy `vscode`. */
export function adapterFor(runtime: Runtime | string | null | undefined): AdapterDefinition | null {
  return runtime && isBuiltinAdapterId(runtime) ? BUILTIN_ADAPTERS[runtime] : null;
}

/** `GET /api/adapters`: id, label, docsUrl, capabilities. No bin, argv, paths or hints (hints may name a login command; none is a secret, but the wizard has no use for them here). */
export function adapterSummaries(): AdapterSummaryDTO[] {
  return BUILTIN_ADAPTER_IDS.map((id) => {
    const d = BUILTIN_ADAPTERS[id];
    return { id: d.id, label: d.label, docsUrl: d.docsUrl, capabilities: { ...d.capabilities } };
  });
}
